import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Chip, CircularProgress, Divider, List,
  ListItem, ListItemText, Typography,
} from '@mui/material';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import { useAuth } from '../../Contexts/AuthContext';
import { useKnowledgebase } from '../../Contexts/KnowledgebaseContext';
import { getReleaseNotes } from '../../Helpers/helpApi';

const CHANGE_CATEGORIES = [
  { key: 'new_features', label: 'New Features', color: 'success' },
  { key: 'improvements', label: 'Improvements', color: 'info' },
  { key: 'bug_fixes', label: 'Bug Fixes', color: 'warning' },
];

export default function ReleaseNotesView() {
  const auth = useAuth();
  const { clearUnreadBadge } = useKnowledgebase();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    getReleaseNotes(auth.activeUser?.accessToken)
      .then((res) => {
        if (res?.data && Array.isArray(res.data)) {
          setNotes(res.data);
        }
      })
      .catch(() => setError('Failed to load release notes.'))
      .finally(() => setLoading(false));

    // Clear the unread badge
    clearUnreadBadge();
  }, [auth.activeUser?.accessToken, clearUnreadBadge]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  if (error) return <Box sx={{ p: 2 }}><Alert severity="error">{error}</Alert></Box>;
  if (notes.length === 0) return (
    <Box sx={{ p: 2, textAlign: 'center' }}>
      <Typography variant="body2" color="text.secondary">No release notes yet.</Typography>
    </Box>
  );

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <NewReleasesIcon color="primary" />
        <Typography variant="h6">What's New</Typography>
      </Box>
      {notes.map((note, idx) => (
        <Box key={note.id || idx} sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="subtitle2" fontWeight={700}>v{note.version}</Typography>
            <Typography variant="caption" color="text.secondary">
              {note.release_date ? new Date(note.release_date).toLocaleDateString() : ''}
            </Typography>
          </Box>
          {CHANGE_CATEGORIES.map(({ key, label, color }) => {
            const items = note.changes?.[key];
            if (!items || items.length === 0) return null;
            return (
              <Box key={key} sx={{ mb: 1.5 }}>
                <Chip label={label} color={color} size="small" sx={{ mb: 0.75 }} />
                <List dense disablePadding>
                  {items.map((item, i) => (
                    <ListItem key={i} disablePadding sx={{ pl: 1 }}>
                      <ListItemText
                        primary={`• ${item}`}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            );
          })}
          {idx < notes.length - 1 && <Divider sx={{ mt: 2 }} />}
        </Box>
      ))}
    </Box>
  );
}
